import { isDefined } from "@clipboard-health/util-ts";
import { IonAlert, IonBadge, IonItem, IonLabel, IonList, IonPage, IonText } from "@ionic/react";
import { HorizontalReferralBanners } from "@src/app/account/HorizontalReferralBanners";
import { useLicenseCount } from "@src/app/licenseManager/hooks/useLicenseCount";
import { getLicensesSummaryText } from "@src/app/licenseManager/utils";
import { useGetRequirementStatus } from "@src/appV2/Accounts/Documents";
import { REFER_AND_EARN_WITH_ACCOUNTS_PATH } from "@src/appV2/Accounts/ReferAndEarn/paths";
import { WORKPLACE_REFERRALS_WITH_ACCOUNTS_PATH } from "@src/appV2/Accounts/WorkplaceReferrals";
import { useWorkerToWorkplaceAffiliateExperiment } from "@src/appV2/Accounts/WorkplaceReferrals/hooks/useWorkerToWorkplaceAffiliateExperiment";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { AppBarHeader, PageWithHeader } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { logApiFailureEvent } from "@src/lib/analytics";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import request from "superagent";

import { updateShowReferralAlert } from "./api";
import { MyAccountProps } from "./model";
import { Version } from "./version";
import { REFERRALS } from "../../constants";
import { USER_EVENTS } from "../../constants/userEvents";
import { useShouldBeOnboarded } from "../onboardingStripe/hooks";
import { setNotificationPreference } from "../privacyPolicy/notificationPreference/api";
import { useSession } from "../store/helperHooks";
import { useDefinedEnv } from "../store/helperHooks/sessionHelpers";
import { updateAgent } from "../store/session";
import { ActionType } from "../store/session/session.model";
import { Store } from "../store/store.model";
import { getAuthHeader } from "../superagent";

export function MyAccount(props: MyAccountProps) {
  const { logout } = props;
  const history = useHistory();
  const match = useRouteMatch();
  const dispatch = useDispatch();
  const [isLogoutConfirm, setIsLogoutConfirm] = useState<boolean>(false);
  const [isClosed, setClosed] = useState<boolean>(false);
  const [pnChecked, setPNChecked] = useState(false);
  const [pushFlag, setPushFlag] = useState<boolean>(false);
  const [isNotificationAlertShown, setNotificationAlertShown] = useState<boolean>(true);
  const { userId, agent } = useSession();
  const env = useDefinedEnv();
  const unreadCount = useSelector((state: Store) => state.notificationStore.unreadCount);

  const { hasUnreadSupportMessage } = useSelector((state: Store) => state.notificationStore);

  const shouldBeOnboardedInPaymentService = useShouldBeOnboarded();
  const { licensesCount } = useLicenseCount();

  const {
    totalCountOfMandatoryRequirements = 0,
    countOfPendingOrCompletedMandatoryRequirements = 0,
  } = useGetRequirementStatus(
    {
      workerId: userId,
    },
    {
      enabled: isDefined(userId),
    }
  );

  const ldFlags = useCbhFlags();

  const workplaceReferralsConfig = ldFlags[CbhFeatureFlag.WORKPLACE_REFERRALS] ?? {};
  const isWorkerToWorkplaceReferralsEnabled = workplaceReferralsConfig.workerToWorkplace ?? false;
  const isWorkerToWorkplaceAffiliateProgramEnabled = useWorkerToWorkplaceAffiliateExperiment();

  const enabledWorkerToWorkerReferralBanners = useCbhFlag(
    CbhFeatureFlag.WORKER_TO_WORKER_REFERRAL_ENABLED_BANNERS,
    {
      defaultValue: { enterReferralCodeBannerIsVisible: false, referAndEarnBannerIsVisible: false },
    }
  );
  const { referAndEarnBannerIsVisible } = enabledWorkerToWorkerReferralBanners;

  const pendingActions =
    totalCountOfMandatoryRequirements !== countOfPendingOrCompletedMandatoryRequirements;

  useEffect(() => {
    if (!env) {
      return;
    }
    setPNChecked(Boolean(env?.notificationPreferences?.push));
    setPushFlag(Boolean(env?.PushNotificationOnAccountTab));
    setClosed(true);
  }, [env]);

  useEffect(() => {
    if (!agent) {
      return;
    }
    if (agent.showReferralAlert) {
      updateShowReferralAlert(agent.userId, { showReferralAlert: false }).then(() => {
        dispatch(updateAgent({ showReferralAlert: false }));
      });
    }
    setNotificationAlertShown(Boolean(agent?.isNotificationAlertShown));
  }, [agent, dispatch]);

  useLogEffect(USER_EVENTS.VIEWED_ACCOUNT);

  const licensesSummaryText = useMemo(() => {
    return getLicensesSummaryText(licensesCount);
  }, [licensesCount]);

  const logMenuItemTapped = (menuItemName: string) => {
    logEvent(USER_EVENTS.TAPPED_ACCOUNT_MENU_ITEM, { menuItemName });
  };

  const closeConfirmModal = (isLogout: boolean) => () => {
    setIsLogoutConfirm(false);
    if (isLogout) {
      logout();
    }
  };

  const setNotificationPopUpShown = async () => {
    const { body: agent } = await request
      .post(`${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/setNotificationAlertShown`)
      .set(await getAuthHeader());
    dispatch({
      type: ActionType.UPDATE_AGENT,
      data: { agent },
    });
  };

  const dismissAlert = async () => {
    try {
      setClosed(false);
      setNotificationPopUpShown();
      await setNotificationPreference({
        notificationPreference: { key: "push", value: true },
      });
    } catch (error) {
      logApiFailureEvent(error);
    }
  };

  const onboardingPaymentColor = shouldBeOnboardedInPaymentService ? "danger" : undefined;
  // FIXME @HaileyesusZ: This ref will be removed once the workplace and worker referral experiments are
  // done and the markets are identified.
  // Right now, some markets are shared and we want to randomly order the banners in My Accounts page.
  const workerBannerIsShownFirstRef = useRef(Boolean(Math.round(Math.random())));

  return (
    <IonPage>
      <PageWithHeader
        appBarHeader={<AppBarHeader title="My Account" />}
        containerVariant="without-margin"
        containerMaxWidth="md"
      >
        {(isWorkerToWorkplaceReferralsEnabled || referAndEarnBannerIsVisible) && (
          <HorizontalReferralBanners
            isWorkerToWorkplaceReferralsEnabled={isWorkerToWorkplaceReferralsEnabled}
            referAndEarnBannerIsVisible={referAndEarnBannerIsVisible}
            workerBannerIsShownFirst={workerBannerIsShownFirstRef.current}
          />
        )}
        <IonList>
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Profile");
              history.push(`${match.url}/profile`);
            }}
            detail
          >
            <IonLabel>
              <h4>Profile</h4>
              {!agent || agent.isProfilePictureUploaded ? (
                <p>View your profile</p>
              ) : (
                <IonText color="danger">
                  <p>Your profile photo is missing!</p>
                </IonText>
              )}
            </IonLabel>
          </IonItem>
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Address");
              history.push(`${match.url}/address`);
            }}
            detail
          >
            <IonLabel>
              <h4>Address</h4>
              <p>Manage your home address</p>
            </IonLabel>
          </IonItem>
          {
            <IonItem
              button
              onClick={() => {
                logMenuItemTapped("Licenses");
                history.push(`${match.url}/licenses`);
              }}
              detail
            >
              <IonLabel>
                <h4>Licenses</h4>
                {licensesSummaryText?.length ? (
                  licensesSummaryText.map((summaryText, index) => {
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={index} style={{ fontSize: "12px" }}>
                        {!!index && ", "}
                        <span style={{ whiteSpace: "nowrap" }}>{summaryText}</span>
                      </span>
                    );
                  })
                ) : (
                  <p>Manage Licenses</p>
                )}
              </IonLabel>
            </IonItem>
          }
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Documents");
              history.push(`${match.url}/documents`);
            }}
            detail
          >
            <IonLabel>
              <h4>Documents</h4>
              {pendingActions ? (
                <IonText color="danger">
                  <p>Some documents are missing or expired!</p>
                </IonText>
              ) : (
                <p>Manage your documents</p>
              )}
            </IonLabel>
          </IonItem>
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Payroll");
              history.push(`${match.url}/payroll`);
            }}
            detail
          >
            <IonLabel color={onboardingPaymentColor}>
              <h4>Payroll</h4>
              <p>Your weekly earnings</p>
            </IonLabel>
          </IonItem>
          {isWorkerToWorkplaceReferralsEnabled && isWorkerToWorkplaceAffiliateProgramEnabled && (
            <IonItem
              button
              onClick={() => {
                logMenuItemTapped("Facility Affiliate Program");
                history.push(WORKPLACE_REFERRALS_WITH_ACCOUNTS_PATH);
              }}
              detail
            >
              <IonLabel>
                {/* FIXME: Use cbh-core Typography/Text.tsx when we migrate account page to use MUI */}
                <h4>Facility Affiliate Program</h4>
                <p>Earn up to $1,000 for every facility you refer</p>
              </IonLabel>
            </IonItem>
          )}
          {isWorkerToWorkplaceReferralsEnabled && !isWorkerToWorkplaceAffiliateProgramEnabled ? (
            <IonItem
              button
              onClick={() => {
                logMenuItemTapped("Refer and Earn");
                history.push(REFER_AND_EARN_WITH_ACCOUNTS_PATH);
              }}
              detail
            >
              <IonLabel>
                {/* FIXME: Use cbh-core Typography/Text.tsx when we migrate account page to use MUI */}
                <h4>Refer a Friend or Facility</h4>
                <p>Earn money on your referrals</p>
              </IonLabel>
            </IonItem>
          ) : (
            <IonItem
              button
              onClick={() => {
                logMenuItemTapped("Refer a Friend");
                history.push(
                  referAndEarnBannerIsVisible
                    ? DeprecatedGlobalAppV1Paths.REFERRAL_V2
                    : DeprecatedGlobalAppV1Paths.REFERRAL
                );
              }}
              detail
            >
              <IonLabel>
                <h4>{REFERRALS.REFER_A_FRIEND}</h4>
                <p>Earn a bonus when they work their first shift</p>
              </IonLabel>
            </IonItem>
          )}
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Work with Friends");
              history.push(`${match.url}/friends`);
            }}
            detail
          >
            <IonLabel>
              <div className="work-with-friends-label-container">
                {/* FIXME: Use cbh-core Typography/Text.tsx when we migrate account page to use MUI */}
                <h4>Work with Friends</h4>
              </div>
              <p>Add friends so you can see the shifts they booked</p>
            </IonLabel>
          </IonItem>
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Privacy and Notifications");
              history.push(`${match.url}/privacy-notification-setting`);
            }}
            detail
          >
            <IonLabel>
              <h4>Privacy and Notifications</h4>
            </IonLabel>
          </IonItem>
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Help Center");
              // TODO:
              // We've merged the separate 'logout' and 'help center' tap events into a single event named 'tapped account menu item'
              // Once we update our Metabase metrics (if having), which currently relies on these events, we can safely remove this
              logEvent(USER_EVENTS.OPENED_HELP_CENTER);
              history.push(`${match.url}/needhelp`);
            }}
            detail
          >
            <IonLabel>
              <h4 className={hasUnreadSupportMessage ? "notification-dot" : ""}>Help Center</h4>
            </IonLabel>
            {unreadCount > 0 && (
              <IonBadge color="danger" mode="ios">
                {unreadCount}
              </IonBadge>
            )}
          </IonItem>
          <IonItem
            button
            onClick={() => {
              logMenuItemTapped("Logout");
              // TODO:
              // We've merged the separate 'logout' and 'help center' tap events into a single event named 'tapped account menu item'
              // Once we update our Metabase metrics (if having), which currently relies on these events, we can safely remove this
              logEvent(USER_EVENTS.TAPPED_LOGOUT);
              setIsLogoutConfirm(true);
            }}
            detail
          >
            <IonLabel>
              <h4>Logout</h4>
            </IonLabel>
          </IonItem>
        </IonList>
        <Version />
        <IonAlert
          message="Are you sure you want to logout?"
          isOpen={isLogoutConfirm}
          buttons={[
            {
              text: "Cancel",
              role: "cancel",
              handler: closeConfirmModal(false),
            },
            {
              text: "Ok",
              handler: closeConfirmModal(true),
            },
          ]}
        />

        <IonAlert
          isOpen={!pushFlag && !isNotificationAlertShown && !pnChecked && isClosed}
          onDidDismiss={dismissAlert}
          header="Notifications"
          message="For this app to work, we'll need push notifications to notify you of your upcoming shifts, open shifts and raffles."
          backdropDismiss={false}
          buttons={["Ok"]}
        />
      </PageWithHeader>
    </IonPage>
  );
}
